import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, addDoc, serverTimestamp, runTransaction, deleteDoc, query, orderBy } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Save, Send, MoreVertical, Copy, Trash2, CheckCircle, Search } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';
import PartsConfigTable from './components/PartsConfigTable.js';

const CreateInvTransfer = ({ db, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [firms, setFirms] = useState([]);
  const [bomDatabase, setBomDatabase] = useState([]);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useClickOutside(() => setShowMoreMenu(false));

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));

  const [formData, setFormData] = useState({
    firmId: '',
    slipNo: '',
    slipDate: new Date().toISOString().split('T')[0],
    txnType: 'Transfer',
    reasonCode: 'SAL2RNT',
    mocNo: '',
    mocDate: '',
    fromWarehouse: 'Bhiwandi',
    toWarehouse: 'Ahmedabad',
    requestedBy: 'Rajesh Kumar',
    department: 'Operations',
    approvedBy: 'Mohit Suthar',
    approvalDate: new Date().toISOString().split('T')[0],
    businessJustification: '',
    referenceNo: '', 
    remarks: '',
    dispatchRef: '', 
    dispatchDate: new Date().toISOString().split('T')[0],
    receivedBy: '',
    receivedDate: '',
    storesVerification: 'Susheel Sahu',
    postingRef: '',
    preparedBy: 'Rajesh Kumar',
    verifiedBy: 'Mohit Suthar',
    status: 'Draft',
    multiplier: 1,
    components: [],
    controlChecklist: {
      mocAttached: false,
      statusUpdated: false,
      postedInLog: false
    }
  });

  const warehouses = ["Bhiwandi", "Ahmedabad", "Delhi", "Jaipur", "Hyderabad"];
  const requestedByOptions = ["Rajesh Kumar", "Ghanshyam Prajapt", "Rajesh Soni"];
  const departments = ["Operations", "Sales", "Accounts", "Warehouse"];
  const approvedByOptions = ["Mohit Suthar", "Ghanshyam Prajapat", "Rajesh Soni", "Jasveer Singh"];
  const storesVerificationOptions = ["Susheel Sahu", "Shahid", "Govind", "Shakti"];
  const preparedByOptions = ["Rajesh Kumar", "Ghanshyam Prajapt", "Ankit Jangir"];
  const verifiedByOptions = ["Mohit Suthar", "Ghanshyam Prajapat", "Rajesh Soni", "Jasveer Singh"];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        const firmsList = firmSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFirms(firmsList);
        
        const prodSnap = await getDocs(query(collection(db, "products"), orderBy("sku", "asc")));
        const prodList = prodSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBomDatabase(prodList);

        if (id) {
          const docRef = doc(db, "inventory_transfers", id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setFormData({ ...formData, ...docSnap.data() });
          }
        } else if (location.state?.duplicateData) {
            const { id: _, slipNo: __, status: ___, createdAt: ____, updatedAt: _____, ...duplicateData } = location.state.duplicateData;
            setFormData(prev => ({
                ...prev,
                ...duplicateData,
                slipDate: new Date().toISOString().split('T')[0],
                status: 'Draft'
            }));
        } else if (firmsList.length > 0) {
          setFormData(prev => ({ ...prev, firmId: firmsList[0].id }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id]);

  useEffect(() => {
    if (formData.status === 'Draft' && formData.firmId && formData.slipDate) {
      generateSlipNo();
    }
  }, [formData.firmId, formData.slipDate, formData.status]);

  const generateSlipNo = async () => {
    const selectedFirm = firms.find(f => f.id === formData.firmId);
    if (!selectedFirm) return;

    const shortCode = selectedFirm.short_code || selectedFirm.name?.substring(0, 3).toUpperCase() || 'GEN';
    const dateStr = formData.slipDate.split('-').reverse().join('');
    const counterKey = `inv_transfer_${shortCode}_${dateStr}`;
    const sequencesRef = doc(db, "counters", "inv_transfers");

    try {
      const count = await runTransaction(db, async (transaction) => {
        const sequenceDoc = await transaction.get(sequencesRef);
        let currentCount = 1;
        if (sequenceDoc.exists() && sequenceDoc.data()[counterKey]) {
          currentCount = sequenceDoc.data()[counterKey] + 1;
        }
        return currentCount;
      });
      setFormData(prev => ({ ...prev, slipNo: `TS/${shortCode}/${dateStr}-${count}` }));
    } catch (e) {
      console.error("Error generating slip no:", e);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChecklistChange = (name) => {
    setFormData(prev => ({
      ...prev,
      controlChecklist: {
        ...prev.controlChecklist,
        [name]: !prev.controlChecklist[name]
      }
    }));
  };

  const handleComponentChange = (index, field, value) => {
    const updatedComponents = [...formData.components];
    updatedComponents[index][field] = value;
    setFormData({ ...formData, components: updatedComponents });
  };

  const handleComponentDelete = (index) => {
    const updatedComponents = formData.components.filter((_, i) => i !== index);
    setFormData({ ...formData, components: updatedComponents });
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (value.length > 1) {
      const filtered = bomDatabase.filter(p => 
        p.name.toLowerCase().includes(value.toLowerCase()) || 
        p.sku.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProducts(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredProducts([]);
      setShowSuggestions(false);
    }
  };

  const addComponent = (product) => {
    let componentsToAdd = [];
    if (product.type === 'preset') {
      componentsToAdd = product.presetComponents.map(pc => {
        const compData = bomDatabase.find(c => c.sku === pc.sku);
        return {
          ...compData,
          baseQty: pc.qty,
          id: `${product.sku}-${pc.sku}-${Date.now()}`
        };
      });
    } else {
      componentsToAdd.push({
        ...product,
        baseQty: 1,
        id: `${product.sku}-${Date.now()}`
      });
    }

    const newComponents = [...formData.components];
    componentsToAdd.forEach(newComp => {
      const existingIdx = newComponents.findIndex(c => c.sku === newComp.sku);
      if (existingIdx > -1) {
        newComponents[existingIdx].baseQty = (Number(newComponents[existingIdx].baseQty) || 0) + Number(newComp.baseQty);
      } else {
        newComponents.push(newComp);
      }
    });

    setFormData({ ...formData, components: newComponents });
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleAction = async (newStatus) => {
    if (formData.components.length === 0 && newStatus === 'Confirmed') {
        alert("Please add at least one component before confirming.");
        return;
    }

    setIsSaving(true);
    try {
      let finalSlipNo = formData.slipNo;

      if (!id) {
        const selectedFirm = firms.find(f => f.id === formData.firmId);
        const shortCode = selectedFirm.short_code || 'GEN';
        const dateStr = formData.slipDate.split('-').reverse().join('');
        const counterKey = `inv_transfer_${shortCode}_${dateStr}`;
        const sequencesRef = doc(db, "counters", "inv_transfers");

        finalSlipNo = await runTransaction(db, async (transaction) => {
          const sequenceDoc = await transaction.get(sequencesRef);
          let count = 1;
          if (sequenceDoc.exists() && sequenceDoc.data()[counterKey]) {
            count = sequenceDoc.data()[counterKey] + 1;
          }
          transaction.set(sequencesRef, { [counterKey]: count }, { merge: true });
          return `TS/${shortCode}/${dateStr}-${count}`;
        });
      }

      const finalData = {
        ...formData,
        slipNo: finalSlipNo,
        status: newStatus,
        updatedAt: serverTimestamp(),
        createdAt: formData.createdAt || serverTimestamp(),
        createdBy: formData.createdBy || user?.uid || 'system'
      };

      if (id) {
        await updateDoc(doc(db, "inventory_transfers", id), finalData);
      } else {
        await addDoc(collection(db, "inventory_transfers"), finalData);
      }

      alert(`Inventory Transfer ${newStatus === 'Confirmed' ? 'confirmed' : 'saved as draft'} successfully!`);
      navigate('/ops/inventory-transfer');
    } catch (error) {
      console.error("Error saving inventory transfer:", error);
      alert("Failed to save: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this transfer?")) {
      try {
        await deleteDoc(doc(db, "inventory_transfers", id));
        navigate('/ops/inventory-transfer');
      } catch (error) {
        alert("Error deleting: " + error.message);
      }
    }
  };

  const handleDuplicate = () => {
    const { id: _, slipNo: __, status: ___, createdAt: ____, updatedAt: _____, ...duplicateData } = formData;
    setFormData({
      ...duplicateData,
      slipDate: new Date().toISOString().split('T')[0],
      status: 'Draft'
    });
    navigate('/ops/inventory-transfer/new');
    setShowMoreMenu(false);
  };

  if (loading) return <Spinner />;

  const sectionStyle = { backgroundColor: 'white', padding: '24px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb' };
  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' };
  const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '6px' };
  const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#374151' };
  const inputStyle = { padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const checkboxLabelStyle = { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#374151', cursor: 'pointer', padding: '8px 0' };
  const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '4px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', zIndex: 1000, maxHeight: '300px', overflowY: 'auto' };
  const moreMenuStyle = { position: 'absolute', top: '100%', right: 0, width: '160px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '4px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden' };
  const dropdownItemStyle = { padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '14px' };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/inventory-transfer')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{id ? 'Edit Transfer' : 'New Transfer'}</h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>{formData.slipNo || 'Draft'}</p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {formData.status === 'Draft' && (
            <>
              <button onClick={() => handleAction('Draft')} disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', fontWeight: '600', cursor: 'pointer' }}>
                <Save size={18} /> Save Draft
              </button>
              <button onClick={() => handleAction('Confirmed')} disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: 'white', fontWeight: '600', cursor: 'pointer' }}>
                <Send size={18} /> Confirm
              </button>
            </>
          )}
          {formData.status === 'Confirmed' && (
             <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={18}/> CONFIRMED
             </span>
          )}
          
          <div style={{ position: 'relative' }} ref={moreMenuRef}>
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer' }}>
              <MoreVertical size={20} />
            </button>
            {showMoreMenu && (
              <div style={moreMenuStyle}>
                <button onClick={handleDuplicate} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', textAlign: 'left' }} className="hover-highlight">
                  <Copy size={16} /> Duplicate
                </button>
                {id && (
                  <button onClick={handleDelete} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', textAlign: 'left', color: '#ef4444' }} className="hover-highlight">
                    <Trash2 size={16} /> Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={gridStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Firm Selection</label>
            <select name="firmId" value={formData.firmId} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Slip No.</label>
            <input type="text" value={formData.slipNo} readOnly style={{ ...inputStyle, backgroundColor: '#f9fafb', color: '#6b7280' }} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Slip Date</label>
            <input type="date" name="slipDate" value={formData.slipDate} onChange={handleChange} style={inputStyle} disabled={id && formData.status === 'Confirmed'} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Txn Type</label>
            <select name="txnType" value={formData.txnType} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              <option value="Transfer">Transfer</option>
              <option value="Conversion">Conversion</option>
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Reason Code</label>
            <select name="reasonCode" value={formData.reasonCode} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              <option value="SAL2RNT">SAL2RNT</option>
              <option value="RNT2DMG">RNT2DMG</option>
              <option value="IBTRSNFR">IBTRSNFR</option>
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>MOC No.</label>
            <input type="text" name="mocNo" value={formData.mocNo} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>MOC Date</label>
            <input type="date" name="mocDate" value={formData.mocDate} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} />
          </div>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={gridStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>From Warehouse</label>
            <select name="fromWarehouse" value={formData.fromWarehouse} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>To Warehouse</label>
            <select name="toWarehouse" value={formData.toWarehouse} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Requested By</label>
            <select name="requestedBy" value={formData.requestedBy} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              {requestedByOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Department</label>
            <select name="department" value={formData.department} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Approved By</label>
            <select name="approvedBy" value={formData.approvedBy} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>
              {approvedByOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Approval Date</label>
            <input type="date" name="approvalDate" value={formData.approvalDate} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} />
          </div>
        </div>
        <div style={{ ...inputGroupStyle, marginTop: '20px' }}>
          <label style={labelStyle}>Business Justification</label>
          <textarea name="businessJustification" value={formData.businessJustification} onChange={handleChange} style={{ ...inputStyle, minHeight: '80px' }} disabled={formData.status === 'Confirmed'} />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={gridStyle}>
          <div style={inputGroupStyle}><label style={labelStyle}>Ref No.</label><input type="text" name="referenceNo" value={formData.referenceNo} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} /></div>
          <div style={inputGroupStyle}><label style={labelStyle}>Dispatch Ref</label><input type="text" name="dispatchRef" value={formData.dispatchRef} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} /></div>
          <div style={inputGroupStyle}><label style={labelStyle}>Dispatch Date</label><input type="date" name="dispatchDate" value={formData.dispatchDate} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} /></div>
          <div style={inputGroupStyle}><label style={labelStyle}>Received By</label><input type="text" name="receivedBy" value={formData.receivedBy} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} /></div>
          <div style={inputGroupStyle}><label style={labelStyle}>Received Date</label><input type="date" name="receivedDate" value={formData.receivedDate} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} /></div>
          <div style={inputGroupStyle}><label style={labelStyle}>Stores Verification</label><select name="storesVerification" value={formData.storesVerification} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>{storesVerificationOptions.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
          <div style={inputGroupStyle}><label style={labelStyle}>Posting Ref</label><input type="text" name="postingRef" value={formData.postingRef} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'} /></div>
        </div>
        <div style={{ ...inputGroupStyle, marginTop: '20px' }}><label style={labelStyle}>Remarks</label><textarea name="remarks" value={formData.remarks} onChange={handleChange} style={{ ...inputStyle, minHeight: '60px' }} disabled={formData.status === 'Confirmed'} /></div>

        <div style={{ marginTop: '30px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
          {formData.status === 'Draft' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ ...inputGroupStyle, flex: 1, minWidth: '300px', position: 'relative' }} ref={searchRef}>
                <label style={labelStyle}>Search Presets/Components</label>
                <div style={{ position: 'relative' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => handleSearch(e.target.value)} onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)} style={{ ...inputStyle, paddingLeft: '40px' }} />
                </div>
                {showSuggestions && filteredProducts.length > 0 && (
                    <div style={dropdownStyle}>
                    {filteredProducts.map(p => (
                        <div key={p.id} style={dropdownItemStyle} onClick={() => addComponent(p)} className="hover-highlight">
                        <div style={{ fontWeight: '600' }}>{p.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>SKU: {p.sku}</div>
                        </div>
                    ))}
                    </div>
                )}
                </div>
                <div style={{ ...inputGroupStyle, width: '120px' }}><label style={labelStyle}>Multiplier</label><input type="number" name="multiplier" value={formData.multiplier} onChange={handleChange} style={inputStyle} min="1" /></div>
            </div>
          )}

          <PartsConfigTable mode="transfer" components={formData.components} multiplier={formData.multiplier} onChange={handleComponentChange} onDelete={handleComponentDelete} hideActions={formData.status === 'Confirmed'} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div style={sectionStyle}>
          <h3 style={labelStyle}><CheckCircle size={18} color="#2563eb" /> Control Checklist</h3>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {Object.keys(formData.controlChecklist).map(key => (
              <label key={key} style={checkboxLabelStyle}>
                <input type="checkbox" checked={formData.controlChecklist[key]} onChange={() => handleChecklistChange(key)} disabled={formData.status === 'Confirmed'} />
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </label>
            ))}
          </div>
        </div>
        <div style={sectionStyle}>
          <h3 style={labelStyle}>Authorization</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={inputGroupStyle}><label style={labelStyle}>Prepared By</label><select name="preparedBy" value={formData.preparedBy} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>{preparedByOptions.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
            <div style={inputGroupStyle}><label style={labelStyle}>Verified By</label><select name="verifiedBy" value={formData.verifiedBy} onChange={handleChange} style={inputStyle} disabled={formData.status === 'Confirmed'}>{verifiedByOptions.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvTransfer;